import { CoveringType } from 'src/app/models/interfaces/GenericInterfaces';
import { CoveringDTO } from 'src/app/models/product-models/DTOs/CoveringDTO';
import { TextureDataDTO } from 'src/app/models/product-models/DTOs/TextureDataDTO';
import { WallDTO } from 'src/app/models/product-models/DTOs/WallDto';
import { Utility } from 'src/app/models/product-models/views/Utility';
import { UtilityGroup } from '../../../interfaces/ProductGroup';
import { Corner } from './corner';
import { Floorplan } from './floorplan';
import { HalfEdge } from './half_edge';
import { Fn, Point, Utils } from './utils';
import { WallObject } from './wall_object';

const WALL_LENGTH_ERROR_TRESHOLD = 0.1;
const MAX_BINARY_ITERATION_COUNT = 5000;
const EXTRA_OFFSET = 500; //zato ker če smo preblizu lahko da nikol nebo najdo

/**
 * A Wall is the basic element to create Rooms.
 *
 * Walls consists of two half edges.
 */
export class Wall {
  public wallTexture = new CoveringDTO(
    CoveringType.Texture,
    new TextureDataDTO(
      'assets/textures/materials/wall.webp',
      undefined,
      undefined,
      'assets/textures/materials/wall_normal.webp',
      0.7,
      0.3,
      undefined,
      undefined,
      undefined,
      [10, 10],
    ),
  );

  /** The unique id of each wall. */
  private _uuid: string;

  private _id?: string;

  /** Front is the plane from start to end. */
  public frontEdge: HalfEdge | null = null;

  /** Back is the plane from end to start. */
  public backEdge: HalfEdge | null = null;

  /** */
  public orphan = false;

  /** The front-side texture. */
  public frontTexture = this.wallTexture;

  /** The back-side texture. */
  public backTexture = this.wallTexture;

  /** Wall thickness. */
  private _thickness = 20;

  /** Wall height. */
  private _height = 250;

  /** Actions to be applied after movement. */
  private _movedCallbacks: Fn[] = [];

  /** Actions to be applied on removal. */
  private _deletedCallbacks: Fn[] = [];

  /** Actions to be applied explicitly. */
  private _actionCallbacks: Fn[] = [];

  /** Objects attached to this wall. */
  private _objects: WallObject[] = [];

  /** Utilities assigned to this wall. */
  private _utilities: Utility[] = [];

  /**
   * Constructs a new wall.
   * @param start Start corner.
   * @param end End corner.
   */
  constructor(
    private floorplan: Floorplan,
    private start: Corner,
    private end: Corner,
  ) {
    this._uuid = this.getUuid();
    this.start.attachStart(this);
    this.end.attachEnd(this);

    this.initDimensionContainers();
  }

  private initDimensionContainers() {
    const container = document.createElement('input');
    container.addEventListener('input', this.updateDimensions);
    container.addEventListener('change', this.confirmDimensionUpdate);

    container.classList.add('dimensions');
    container.id = this._uuid;
    document.getElementById('floorplanner')?.append(container);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onUtilityClick = (el: any) => {
    const group = el.target.utility as UtilityGroup;

    if (group) {
      if (this.hasUtility(group.utilities[0])) {
        this.removeUtility(group.utilities[0]);

        if (el.target instanceof HTMLImageElement) {
          el.target.parentElement.classList.remove('active-selection');
        } else {
          el.target.classList.remove('active-selection');
        }
      } else {
        this.addUtility(group.utilities[0]);

        if (el.target instanceof HTMLImageElement) {
          el.target.parentElement.classList.add('active-selection');
        } else {
          el.target.classList.add('active-selection');
        }
      }
    }
  };

  public setTexture(customTexture?: CoveringDTO) {
    const texture = customTexture || this.wallTexture;
    this.wallTexture = texture;
    this.frontTexture = texture;
    this.backTexture = texture;
  }

  public toDTO() {
    let interiorCorners = [],
      exteriorCorners = [];

    const angle = this.getAngle();
    const offsetX = Math.cos(angle) * (this._thickness / 2);
    const offsetY = Math.sin(angle) * (this._thickness / 2);
    this._objects.forEach((obj) => obj.update(offsetX, offsetY));

    let type = 'exterior';

    if (this.backEdge) {
      interiorCorners.push(this.backEdge.interiorEnd(), this.backEdge.interiorStart());
      exteriorCorners.push(this.backEdge.exteriorEnd(), this.backEdge.exteriorStart());
    } else if (this.frontEdge) {
      interiorCorners.push(this.frontEdge.interiorEnd(), this.frontEdge.interiorStart());
      exteriorCorners.push(this.frontEdge.exteriorEnd(), this.frontEdge.exteriorStart());
    } else {
      interiorCorners.push(
        {
          x: this.getStartX() + offsetX,
          y: this.getStartY() + offsetY,
        } as Point,
        {
          x: this.getEndX() + offsetX,
          y: this.getEndY() + offsetY,
        } as Point,
      );

      exteriorCorners.push(
        {
          x: this.getStartX() - offsetX,
          y: this.getStartY() - offsetY,
        } as Point,
        {
          x: this.getEndX() - offsetX,
          y: this.getEndY() - offsetY,
        } as Point,
      );

      type = 'interior';
    }

    interiorCorners = interiorCorners.map(
      (point) => new Corner(this.floorplan, point.x, point.y, this._height),
    ) as Corner[];
    exteriorCorners = exteriorCorners.map(
      (point) => new Corner(this.floorplan, point.x, point.y, this._height),
    ) as Corner[];

    const utilityIds: number[] = [];
    for (const utility of this._utilities) {
      if (utility.id) utilityIds.push(utility.id);
    }

    const wall = new WallDTO(
      this.getId(),
      type,
      interiorCorners.map((corner) => corner.uuid),
      exteriorCorners.map((corner) => corner.uuid),
      utilityIds,
      this._thickness,
      this.backTexture,
    );

    return {
      wallDTO: wall,
      interiorCorners: interiorCorners,
      exteriorCorners: exteriorCorners,
    };
  }

  private getUuid(): string {
    //return [this.start.uuid, this.end.uuid].join();
    return Utils.guid();
  }

  public addObject(obj: WallObject) {
    this._objects.push(obj);
  }

  public getObjects(): WallObject[] {
    return this._objects;
  }

  public setObjects(value: WallObject[]) {
    this._objects = value;

    for (const object of this._objects) {
      object.wall = this;
    }
  }

  public getUtilities(): Utility[] {
    return this._utilities;
  }

  public setUtilities(util: Utility[]) {
    this._utilities = util;
  }

  public addUtility(util: Utility) {
    this._utilities.push(util);
  }

  public removeUtility(util: Utility) {
    this._utilities = this._utilities.filter((u) => u !== util);
  }

  public hasUtility(util?: Utility) {
    if (!util) return this._utilities.length > 0;
    return this._utilities.map((util) => util.type).includes(util.type);
  }

  public removeObject(obj: WallObject) {
    const index = this._objects.findIndex((key) => key.id === obj.id);
    if (index !== -1) {
      this._objects[index].dispose();
      this._objects.splice(index, 1);
    }
  }

  public findFreeSpotForNewObject(object: WallObject) {
    const step = 0.005;
    let pointOnLine = false;
    let offset = step;

    while (!pointOnLine) {
      const newPoint = Utils.moveAlongLine(
        this.start.getX(),
        this.start.getY(),
        this.end.getX(),
        this.end.getY(),
        offset,
      );

      if (Utils.distance(newPoint.x, newPoint.y, this.end.getX(), this.end.getY()) <= object.height / 2) {
        break;
      }

      object.attach(this, newPoint.x, newPoint.y, this.getAngle());

      if (object.isAttached()) {
        this.addObject(object);
        //this.fireMoved();
        return true;
      } else {
        pointOnLine =
          Utils.pointDistanceFromLine(
            newPoint.x,
            newPoint.y,
            this.start.getX(),
            this.start.getY(),
            this.end.getX(),
            this.end.getY(),
          ) >= step;

        offset += step;
      }
    }

    object.detach();
    return false;
  }

  /**
   * Adjusts the wall’s end so that the wall length comes within
   * WALL_LENGTH_ERROR_TRESHOLD of the desired length.
   * This uses a binary search strategy.
   * @param desiredInnerLength The target length (in same units as getLength()).
   */
  private _binaryAdjust(desiredInnerLength: number, backEdge: HalfEdge): void {
    if (desiredInnerLength === 0) desiredInnerLength = 10;
    let currentInnerLength = backEdge.interiorDistance();
    let low: number;
    let high: number;

    high = currentInnerLength;
    low = currentInnerLength;

    while (high - EXTRA_OFFSET < desiredInnerLength) {
      high *= 2;
    }
    while (low + EXTRA_OFFSET > desiredInnerLength && low > 10) {
      low /= 2;
    }

    let iterCount = 0;

    // Binary search loop:
    while (Math.abs(currentInnerLength - desiredInnerLength) > WALL_LENGTH_ERROR_TRESHOLD) {
      const mid = (high + low) / 2;

      this._setEndByLength(mid);

      currentInnerLength = backEdge.interiorDistance();
      if (currentInnerLength < desiredInnerLength) {
        low = mid;
      } else {
        high = mid;
      }

      iterCount++;
      if (iterCount > MAX_BINARY_ITERATION_COUNT) {
        return;
      }
    }
  }

  /**
   * Helper function that repositions the wall's end corner based on a given length.
   * It assumes the start corner is fixed and the new end is placed along the current
   * wall's direction.
   * @param newLength The new desired wall length.
   */
  private _setEndByLength(newLength: number): void {
    const sx = this.getStartX();
    const sy = this.getStartY();
    const ex = this.getEndX();
    const ey = this.getEndY();

    // Calculate the current direction vector.
    const dx = ex - sx;
    const dy = ey - sy;
    const currentLength = Math.sqrt(dx * dx + dy * dy);
    if (currentLength === 0) return; // Avoid division by zero.

    const ux = dx / currentLength;
    const uy = dy / currentLength;

    // Calculate the new end point along the direction vector.
    const newEx = sx + ux * newLength;
    const newEy = sy + uy * newLength;

    // Update the end corner.
    // (Assuming you can set the x and y directly. If Corner has setters, use those.)
    this.end.x = newEx;
    this.end.y = newEy;
  }

  // In your updateDimensions method you might call the binary adjust:
  public updateDimensions = (input: any) => {
    const value = input.target.value.replace(/[^\d.]/g, '');
    if (!value) return;

    if (!this.backEdge) return;

    const newInnerDistance = Math.round(Number(value) * 100);

    this._binaryAdjust(newInnerDistance, this.backEdge);
  };

  public confirmDimensionUpdate = () => {
    this.fireMoved();
  };

  public getId() {
    return this._uuid;
  }

  public resetFrontBack() {
    this.frontEdge = null;
    this.backEdge = null;
    this.orphan = false;
  }

  public snapToAxis(tolerance: number) {
    // order here is important, but unfortunately arbitrary
    this.start.snapToAxis(tolerance);
    this.end.snapToAxis(tolerance);
  }

  public fireOnMove(func: Fn) {
    this._movedCallbacks.push(func);
  }

  public fireOnDelete(func: Fn) {
    this._deletedCallbacks.push(func);
  }

  public dontFireOnDelete(func: Fn) {
    this._deletedCallbacks.splice(this._deletedCallbacks.indexOf(func), 1);
  }

  public fireOnAction(func: Fn) {
    this._actionCallbacks.push(func);
  }

  public fireAction(action: Fn) {
    //this.action_callbacks.fire(action);
    this._actionCallbacks.forEach((callback: Fn) => callback(action));
  }

  public relativeMove(dx: number, dy: number) {
    this.start.relativeMove(dx, dy);
    this.end.relativeMove(dx, dy);
  }

  public fireMoved() {
    this._objects.forEach((obj) => obj.update());
    this._movedCallbacks.forEach((callback: Fn) => callback());
  }

  public fireRedraw() {
    if (this.frontEdge) {
      //this.frontEdge.redrawCallbacks.fire();
      this.frontEdge.redrawCallbacks.forEach((callback: Fn) => callback());
    }

    if (this.backEdge) {
      //this.backEdge.redrawCallbacks.fire();
      this.backEdge.redrawCallbacks.forEach((callback: Fn) => callback());
    }
  }

  public getStart(): Corner {
    return this.start;
  }

  public getEnd(): Corner {
    return this.end;
  }

  public getStartX(): number {
    return this.start.getX();
  }

  public getEndX(): number {
    return this.end.getX();
  }

  public getStartY(): number {
    return this.start.getY();
  }

  public getEndY(): number {
    return this.end.getY();
  }

  public getThickness() {
    return this._thickness;
  }

  public setThickness(value: number) {
    this._thickness = value;
  }

  public getHeight() {
    return this._height;
  }

  public setHeight(value: number) {
    this._height = value;
  }

  public remove() {
    this.start.detachWall(this);
    this.end.detachWall(this);
    document.getElementById(this._uuid)?.remove();
    for (const obj of this._objects) obj.dispose();
    this._deletedCallbacks.forEach((callback: Fn) => callback(this));
  }

  /** Finds middle point between corners.
   * @returns The middle point.
   */
  public calculateCenterPoint(): Point {
    const x = (this.getStartX() + this.getEndX()) / 2;
    const y = (this.getStartY() + this.getEndY()) / 2;
    return { x: x, y: y };
  }

  public setStart(corner: Corner) {
    this.start.detachWall(this);
    corner.attachStart(this);
    this.start = corner;
    this.fireMoved();
  }

  public setEnd(corner: Corner) {
    this.end.detachWall(this);
    corner.attachEnd(this);
    this.end = corner;
    this.fireMoved();
  }

  public distanceFrom(x: number, y: number): number {
    return Utils.pointDistanceFromLine(x, y, this.getStartX(), this.getStartY(), this.getEndX(), this.getEndY());
  }

  public closestPointFrom(x: number, y: number): Point {
    return Utils.closestPointOnLine(x, y, this.getStartX(), this.getStartY(), this.getEndX(), this.getEndY());
  }

  public getAngle(): number {
    return Utils.wallAngle(this.getStartX(), this.getStartY(), this.getEndX(), this.getEndY());
  }

  public getLength(): number {
    return Utils.distance(this.start.x, this.start.y, this.end.x, this.end.y);
  }

  /** Return the corner opposite of the one provided.
   * @param corner The given corner.
   * @returns The opposite corner.
   */
  private oppositeCorner(corner: Corner): Corner | null {
    if (this.start === corner) {
      return this.end;
    } else if (this.end === corner) {
      return this.start;
    } else {
      throw Error('Wall does not connect to corner');
      //return null;
    }
  }
}
